import React from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Title } from 'chart.js'
import { Chart } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Title
)

export default function ComboChart({
  labels = [],
  bar = { label: 'Bar', data: [], color: '#2563eb' },
  line = { label: 'Line', data: [], color: '#f97316' },
  title,
  height = 320,
  loading = false,
  error = null
}) {
  if (loading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Failed to load chart data</div>
      </div>
    )
  }

  if (!labels || labels.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-loading">No data available</div>
      </div>
    )
  }

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: bar.label || 'Bar',
        data: (bar.data || []).map(v => (Number.isFinite(+v) ? +v : 0)),
        backgroundColor: (bar.color || '#2563eb'),
        borderColor: (bar.color || '#2563eb'),
        borderWidth: 0,
        barThickness: 16,
        maxBarThickness: 28,
        borderRadius: 0,
        order: 1
      },
      {
        type: 'line',
        label: line.label || 'Line',
        data: (line.data || []).map(v => (Number.isFinite(+v) ? +v : 0)),
        borderColor: line.color || '#f97316',
        backgroundColor: 'transparent',
        borderWidth: 3,
        tension: 0.35,
        pointBackgroundColor: line.color || '#f97316',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        order: 0
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: false,
          padding: 16,
          font: { size: 13, weight: '500', family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif' },
          color: '#374151'
        }
      },
      title: {
        display: !!title,
        text: title,
        font: { size: 18, weight: '600', family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif' },
        color: '#111827',
        padding: { top: 16, bottom: 24 }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: true,
        padding: 12
      }
    },
    scales: {
      x: {
        grid: { display: true, color: 'rgba(229, 231, 235, 0.5)', drawBorder: false },
        ticks: {
          font: { size: 12, weight: '500', family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif' },
          color: '#6b7280',
          padding: 8
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(229, 231, 235, 0.5)', drawBorder: false, lineWidth: 1 },
        ticks: {
          font: { size: 12, weight: '500', family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif' },
          color: '#6b7280',
          padding: 8,
          stepSize: 1,
          callback: function(value) {
            // Ensure only whole numbers are displayed on y-axis
            return Number.isInteger(value) ? value : Math.round(value)
          }
        },
        // Set default max value for empty data
        suggestedMax: function(context) {
          const data = context.chart.data.datasets.flatMap(ds => ds.data || [])
          const maxValue = Math.max(...data, 0)
          // If no data or max value is 0, set default scale 0-5
          return maxValue === 0 ? 5 : undefined
        }
      }
    },
    interaction: { intersect: false, mode: 'index' }
  }

  return (
    <div style={{ height, width: '100%' }}>
      <Chart type='bar' data={chartData} options={options} />
    </div>
  )
}
